import React from 'react'
import { ParsedQuery } from 'query-string'
import { Owner } from 'src/Utils/globalEnums'
import {
  ATTESTATION_STATUS,
  AttestationStatusFilter,
  ListVendorsRequest,
  OwnerFilter,
  RiskLevelFilter,
  VendorSort,
  VendorStateFilter,
  VendorStatusFilter,
} from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { BoolValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import {
  GridColumnSortType,
  GridColumnSortVendors,
  SORT_ORDER,
} from 'src/components/Reusable/Grid/GridColumnSort/GridColumnSort.constants'
import { FilterParam } from 'src/components/Reusable/IndexPage/FilterBar/FilterBar.types'
import { queryParamAsArray, queryParamAsString } from '../../Utils/queryParams'
import {
  ATTESTATION_LABEL_TO_STATUS,
  AttestationStatusLabels,
  RISK_TIER_LABEL_TO_LEVEL,
  RiskTierLabels,
  VENDOR_SORT_COLUMNS,
  NO_VENDOR_STATUS_FILTER_VALUE,
  VendorStateLabels,
} from './vendors.constants'
import { AttestationFreshIcon, AttestationStaleIcon } from './vendors.styles'

export const applyVendorsFilters = (
  req: ListVendorsRequest,
  queryParams: ParsedQuery<string>,
  filterType: FilterParam,
): void => {
  const filterParams = queryParamAsArray(queryParams[filterType])

  if (!filterParams?.length) {
    return
  }

  switch (filterType) {
    case FilterParam.RISK_TIER: {
      const riskLevelFilter = new RiskLevelFilter()
      const tiers = (filterParams as RiskTierLabels[]).map(
        (label) => RISK_TIER_LABEL_TO_LEVEL[label],
      )
      tiers.length > 0 && riskLevelFilter.setRiskLevelsList(tiers)
      req.setRiskLevelFilter(riskLevelFilter)
      break
    }
    case FilterParam.OWNER: {
      const ownerFilter = new OwnerFilter()
      const emails = [...filterParams]
      const idx = emails.findIndex(
        (email) => email?.toLowerCase() === Owner.UNASSIGNED_BE,
      )
      if (idx > -1) {
        emails.splice(idx, 1)
        ownerFilter.setOwnerEmailsList(emails)
        ownerFilter.setIncludeUnassigned(true)
      } else {
        ownerFilter.setOwnerEmailsList(emails)
      }
      req.setOwnerFilter(ownerFilter)
      break
    }
    case FilterParam.ASSIGNEE: {
      const emails = [...filterParams]
      const idx = emails.findIndex(
        (email) => email?.toLowerCase() === Owner.UNASSIGNED_BE,
      )
      if (idx > -1) {
        emails.splice(idx, 1)
        req.setAssigneeIncludeUnassigned(true)
      }
      req.setAssigneeEmailsList(emails)
      break
    }
    case FilterParam.VENDOR_STATUS: {
      const statusFilter = new VendorStatusFilter()
      const values = [...filterParams]
      const noStatusIdx = values.indexOf(NO_VENDOR_STATUS_FILTER_VALUE)
      if (noStatusIdx > -1) {
        values.splice(noStatusIdx, 1)
        statusFilter.setIncludeNoStatus(true)
      }
      statusFilter.setStatusIdsList(values)
      req.setStatusFilter(statusFilter)
      break
    }
    case FilterParam.VENDOR_STATE: {
      // Both selected is the same as no filter -- leave active unset so the
      // backend reads it as "any state".
      const wantsActive = filterParams.includes(VendorStateLabels.ACTIVE)
      const wantsInactive = filterParams.includes(VendorStateLabels.INACTIVE)
      if (wantsActive !== wantsInactive) {
        const stateFilter = new VendorStateFilter()
        stateFilter.setActive(new BoolValue().setValue(wantsActive))
        req.setStateFilter(stateFilter)
      }
      break
    }
    case FilterParam.VENDOR_EVALUATION:
      req.setEvaluationStatusFilterList(filterParams.map(Number))
      break
    case FilterParam.ATTESTATION: {
      const attestationFilter = new AttestationStatusFilter()
      const statuses = new Set(
        (filterParams as AttestationStatusLabels[]).map(
          (label) => ATTESTATION_LABEL_TO_STATUS[label],
        ),
      )
      statuses.has(ATTESTATION_STATUS.NONE) &&
        attestationFilter.setIncludeNone(true)
      statuses.has(ATTESTATION_STATUS.STALE) &&
        attestationFilter.setIncludeStale(true)
      statuses.has(ATTESTATION_STATUS.FRESH) &&
        attestationFilter.setIncludeFresh(true)
      statuses.size > 0 && req.setAttestationStatusFilter(attestationFilter)
      break
    }
    default:
      break
  }
}

export const applyVendorsSort = (
  req: ListVendorsRequest,
  queryParams: ParsedQuery<string>,
): void => {
  const sortOrder =
    (queryParamAsString(queryParams.sort_by) as GridColumnSortType) || undefined
  const sortCol =
    (queryParamAsString(queryParams.sort_col) as GridColumnSortVendors) ||
    undefined
  const shouldApplySort = sortOrder && sortCol
  if (!shouldApplySort) {
    return
  }
  const sort = new VendorSort()
  sort.setSortOrder(SORT_ORDER[sortOrder])
  sort.setSortColumn(VENDOR_SORT_COLUMNS[sortCol])
  req.setSort(sort)
}

export const getFreshnessIcon = (isFresh: boolean): JSX.Element => {
  return isFresh ? <AttestationFreshIcon /> : <AttestationStaleIcon />
}

export const getVendorFreshTooltipBody = (
  status: ATTESTATION_STATUS,
): string => {
  switch (status) {
    case ATTESTATION_STATUS.STALE:
      return 'Most recent attestation is out of date'
    case ATTESTATION_STATUS.NONE:
      return 'No attestation for this vendor'
    default:
      return ''
  }
}
